import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
	Box,
	ClickAwayListener,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Paper,
	Popper,
	Typography,
	useMediaQuery
} from '@mui/material';

// third-party
import { FormattedMessage } from 'react-intl';

// project import
import NavItem from './NavItem';
import NavCollapse from './NavCollapse';
import Transitions from 'layout/mantis/components/@extended/Transitions';
import useConfigContext from 'context/useConfigContext';
import { dispatch } from 'layout/mantis/store';
import { activeID } from 'layout/mantis/store/reducers/menu';

// assets
import { DownOutlined, RightOutlined } from '@ant-design/icons';

// types
import { NavItemType } from 'layout/mantis/types/MenuType';
import { RootStateProps } from 'layout/mantis/types/RootType';
import { LAYOUT_CONST } from 'types/ConfigType';

// ==============================|| NAVIGATION - LIST GROUP ||============================== //

interface Props {
	item: NavItemType;
	lastItem: number;
	remItems: NavItemType[];
	lastItemId: string;
	setSelectedItems: React.Dispatch<React.SetStateAction<string | undefined>>;
	selectedItems: string | undefined;
	setSelectedLevel: React.Dispatch<React.SetStateAction<number>>;
	selectedLevel: number;
}

type VirtualElement = {
	getBoundingClientRect: () => ClientRect | DOMRect;
	contextElement?: Element;
};

const PopperStyled = styled(Popper)(({ theme }) => ({
	overflow: 'visible',
	zIndex: 1202,
	minWidth: 180,
	'&:before': {
		content: '""',
		display: 'block',
		position: 'absolute',
		top: 5,
		left: 32,
		width: 12,
		height: 12,
		transform: 'translateY(-50%) rotate(45deg)',
		zIndex: 120,
		borderWidth: '6px',
		borderStyle: 'solid',
		borderColor: `${ theme.palette.background.paper }  transparent transparent ${ theme.palette.background.paper }`
	}
}));

const NavGroup = ({
					  item,
					  lastItem,
					  remItems,
					  lastItemId,
					  setSelectedItems,
					  selectedItems,
					  setSelectedLevel,
					  selectedLevel
				  }: Props) => {
	const theme = useTheme();
	const { pathname } = useLocation();

	const { menuOrientation } = useConfigContext();
	const menu = useSelector((state: RootStateProps) => state.menu);
	const { drawerOpen, selectedID } = menu;

	const downLG = useMediaQuery(theme.breakpoints.down('lg'));

	const [anchorEl, setAnchorEl] = useState<VirtualElement | (() => VirtualElement) | null | undefined>(null);
	const [currentItem, setCurrentItem] = useState(item);

	const openMini = Boolean(anchorEl);

	useEffect(() => {
		if (lastItem) {
			if (item.id === lastItemId) {
				const localItem: any = { ...item };
				const elements = remItems.map((ele: NavItemType) => ele.elements);
				localItem.children = elements.flat(1);
				setCurrentItem(localItem);
			} else {
				setCurrentItem(item);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [item, lastItem, downLG]);

	const checkOpenForParent = (child: NavItemType[], id: string) => {
		child.forEach((ele: NavItemType) => {
			if (ele.children?.length) {
				checkOpenForParent(ele.children, currentItem.id!);
			}
			if (ele.url === pathname) {
				dispatch(activeID(id));
			}
		});
	};
	const checkSelectedOnload = (data: NavItemType) => {
		const childrens = data.children ? data.children : [];
		childrens.forEach((itemCheck: NavItemType) => {
			if (itemCheck.children?.length) {
				checkOpenForParent(itemCheck.children, currentItem.id!);
			}
			if (itemCheck.url === pathname) {
				dispatch(activeID(currentItem.id!));
			}
		});
	};

	useEffect(() => {
		checkSelectedOnload(currentItem);
		if (openMini) setAnchorEl(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, currentItem]);

	const handleClick = (event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined) => {
		if (!openMini) {
			setAnchorEl(event?.currentTarget);
		}
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const Icon = currentItem?.icon!;
	const itemIcon = currentItem?.icon ? (
		<Icon
			style={ {
				fontSize: 20,
				stroke: '1.5',
				color: selectedID === currentItem.id ? theme.palette.primary.main : theme.palette.secondary.dark
			} }
		/>
	) : null;

	const navCollapse = item.children?.map((menuItem, index) => {
		switch (menuItem.type) {
			case 'collapse':
				return (
					<NavCollapse
						key={ menuItem.id }
						menu={ menuItem }
						setSelectedItems={ setSelectedItems }
						setSelectedLevel={ setSelectedLevel }
						selectedLevel={ selectedLevel }
						selectedItems={ selectedItems }
						level={ 1 }
						parentId={ currentItem.id! }
					/>
				);
			case 'item':
				return <NavItem key={ menuItem.id } item={ menuItem } level={ 1 } />;
			default:
				return (
					<Typography key={ menuItem.id } variant='h6' color='error' align='center'>
						Fix - Group Collapse or Items
					</Typography>
				);
		}
	});

	const moreItems = remItems.map((itemRem: NavItemType, i) => (
		<Fragment key={ i }>
			{ itemRem.title && (
				<Typography variant='caption' sx={ { pl: 2 } }>
					{ itemRem.title }
				</Typography>
			) }
			{ itemRem?.elements?.map((menu) => {
				switch (menu.type) {
					case 'collapse':
						return (
							<NavCollapse
								key={ menu.id }
								menu={ menu }
								level={ 1 }
								parentId={ currentItem.id! }
								setSelectedItems={ setSelectedItems }
								setSelectedLevel={ setSelectedLevel }
								selectedLevel={ selectedLevel }
								selectedItems={ selectedItems }
							/>
						);
					case 'item':
						return <NavItem key={ menu.id } item={ menu } level={ 1 } />;
					default:
						return (
							<Typography key={ menu.id } variant='h6' color='error' align='center'>
								Menu Items Error
							</Typography>
						);
				}
			}) }
		</Fragment>
	));

	// menu list collapse & items
	const items = currentItem.children?.map((menu) => {
		switch (menu.type) {
			case 'collapse':
				return (
					<NavCollapse
						key={ menu.id }
						menu={ menu }
						level={ 1 }
						parentId={ currentItem.id! }
						setSelectedItems={ setSelectedItems }
						setSelectedLevel={ setSelectedLevel }
						selectedLevel={ selectedLevel }
						selectedItems={ selectedItems }
					/>
				);
			case 'item':
				return <NavItem key={ menu.id } item={ menu } level={ 1 } />;
			default:
				return (
					<Typography key={ menu.id } variant='h6' color='error' align='center'>
						Menu Items Error
					</Typography>
				);
		}
	});

	const popperId = openMini ? `group-pop-${ item.id }` : undefined;

	return (
		<>
			{ menuOrientation === LAYOUT_CONST.VERTICAL_LAYOUT || downLG ? (
				<List
					subheader={
						item.title &&
						drawerOpen && (
							<Box sx={ { pl: 3, mb: 1.5 } }>
								<Typography variant='subtitle2'
											color={ theme.palette.mode === 'dark' ? 'textSecondary' : 'text.secondary' }>
									{ item.title }
								</Typography>
								{ item.caption && (
									<Typography variant='caption' color='secondary'>
										{ item.caption }
									</Typography>
								) }
							</Box>
						)
					}
					sx={ { mt: drawerOpen && item.title ? 1.5 : 0, py: 0, zIndex: 0 } }
				>
					{ navCollapse }
				</List>
			) : (
				<List>
					<ListItemButton
						selected={ selectedID === currentItem.id }
						sx={ {
							p: 1,
							my: 0.5,
							mr: 1,
							display: 'flex',
							alignItems: 'center',
							backgroundColor: 'inherit',
							'&.Mui-selected': {
								bgcolor: 'transparent'
							}
						} }
						onMouseEnter={ handleClick }
						onClick={ handleClick }
						onMouseLeave={ handleClose }
						aria-describedby={ popperId }
					>
						{ itemIcon && (
							<ListItemIcon sx={ { minWidth: 28 } }>
								{ currentItem.id === lastItemId ?
									<DownOutlined style={ { fontSize: 20, stroke: '1.5' } } /> : itemIcon }
							</ListItemIcon>
						) }
						<ListItemText
							sx={ { mr: 1 } }
							primary={
								<Typography
									variant='body1'
									color={ selectedID === currentItem.id ? theme.palette.primary.main : theme.palette.secondary.dark }
								>
									{ currentItem.id === lastItemId ?
										<FormattedMessage id='More Items' /> : currentItem.title }
								</Typography>
							}
						/>
						{ openMini ? (
							<DownOutlined style={ { fontSize: 16, stroke: '1.5' } } />
						) : (
							<RightOutlined style={ { fontSize: 16, stroke: '1.5' } } />
						) }
						{ anchorEl && (
							<PopperStyled
								id={ popperId }
								open={ openMini }
								anchorEl={ anchorEl }
								placement='bottom-start'
								style={ {
									zIndex: 2001
								} }
							>
								{ ({ TransitionProps }) => (
									<Transitions in={ openMini } { ...TransitionProps }>
										<Paper
											sx={ {
												mt: 0.5,
												py: 1.25,
												boxShadow: theme.shadows[8],
												backgroundImage: 'none'
											} }
										>
											<ClickAwayListener onClickAway={ handleClose }>
												<Box
													sx={ {
														maxHeight: 'calc(100vh - 170px)',
														overflowY: 'auto',
														'&::-webkit-scrollbar': {
															opacity: 0,
															width: 4,
															'&:hover': {
																opacity: 0.7
															}
														},
														'&::-webkit-scrollbar-track': {
															background: 'transparent'
														},
														'&::-webkit-scrollbar-thumb': {
															background: theme.palette.divider,
															borderRadius: 4
														}
													} }
												>
													{ currentItem.id !== lastItemId ? items : moreItems }
												</Box>
											</ClickAwayListener>
										</Paper>
									</Transitions>
								) }
							</PopperStyled>
						) }
					</ListItemButton>
				</List>
			) }
		</>
	);
};

export default NavGroup;
